import axios from 'axios';
import { GATEWAY_URL } from '../utils/constants';
import { getToken } from '../utils/auth';

/*
 * Cliente isolado, de propósito.
 *
 * O `gateway` compartilhado (utils/api.js) desloga o usuário em qualquer 403 e
 * reporta toda falha ao Bugsnag. Como os boletos do SAP são buscados no
 * NavBarProvider — que monta em TODA tela do portal antigo — um 403 aqui
 * derrubaria a sessão do app inteiro em loop. Este cliente carrega os mesmos
 * headers de autenticação, mas nenhum interceptor com efeito colateral: a
 * falha fica contida em quem chamou.
 */
const bankSlipsClient = axios.create({ baseURL: GATEWAY_URL });

bankSlipsClient.interceptors.request.use(config => {
  const token = getToken();
  const tenant = localStorage.getItem('tenant');
  const profileCode = localStorage.getItem('crCodeProfile');
  const ownerEmail = localStorage.getItem('ra');

  if (token) {
    config.headers.Authorization = token;
  }

  if (tenant) {
    config.headers.Tenant = tenant;
  }

  if (profileCode) {
    config.headers.ProfileCode =
      tenant === 'ALL' ? localStorage.getItem('tokenCrCode') : profileCode;
  }

  // Mesma convenção de x-owner já usada em utils/activeSubscriptions.js
  if (ownerEmail) {
    config.headers['x-owner'] = ownerEmail;
  }

  return config;
});

const SAP_CONTRACT_TITLE = 'Boletos';

const priceFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

// O template concatena "R$ " antes do valor, então formatamos só o número.
const formatPrice = value => priceFormatter.format(Number(value) || 0);

/*
 * Formata a data sem passar por `new Date`: "2026-09-19" (sem offset) seria
 * interpretado como UTC e viraria 18/09 no fuso do Brasil.
 */
const formatDueDate = dueDate => {
  if (typeof dueDate !== 'string') {
    return '';
  }

  const [year, month, day] = dueDate.split('T')[0].split('-');

  if (!year || !month || !day) {
    return '';
  }

  return `${day}/${month}/${year}`;
};

/*
 * Dois lugares pedem os mesmos boletos no mesmo carregamento: o NavBarProvider
 * — que monta em toda tela para decidir se exibe o link "Boletos" — e a própria
 * PaymentHistory. Sem dedupe, abrir a tela de boletos dispara duas idas ao SAP,
 * que é lento e cobra por consulta.
 *
 * A promise em voo é compartilhada por todos os chamadores, e a resposta vale
 * por CACHE_TTL_MS. O TTL é curto de propósito: linha digitável e status de
 * pagamento mudam no IntegraSky, então isto é dedupe de carregamento, não cache
 * de dados. Como é variável de módulo, um F5 busca de novo.
 */
const CACHE_TTL_MS = 30000;

let pendingRequest = null;
let requestStartedAt = 0;

export const getSapBankSlips = async () => {
  const isFresh = pendingRequest && Date.now() - requestStartedAt < CACHE_TTL_MS;

  if (!isFresh) {
    requestStartedAt = Date.now();

    // Falha não fica cacheada: o próximo chamador tenta de novo em vez de
    // herdar o erro pelo resto do TTL.
    pendingRequest = bankSlipsClient
      .get('/account/account/bank-slips')
      .then(({ data }) => data)
      .catch(error => {
        pendingRequest = null;
        throw error;
      });
  }

  return pendingRequest;
};

/*
 * Adapta o retorno de /account/account/bank-slips para o mesmo shape que o
 * getUserInvoices devolve, para que o OrderItem renderize as duas origens sem
 * saber de onde vieram. A única diferença é a ação: o SAP não manda URL de PDF,
 * apenas a linha digitável.
 */
export const normalizeSapBankSlips = (contracts = []) => {
  if (!Array.isArray(contracts)) {
    return [];
  }

  const validContracts = contracts.filter(contract => {
    if (!contract) {
      return false;
    }

    if (contract.error) {
      console.error(
        'Contrato de boleto do SAP retornado com erro:',
        contract.error
      );
      return false;
    }

    // Sem boletos não há o que mostrar — e installmentsCount 0 geraria
    // NaN% na barra de progresso do OrderItem.
    return Boolean(contract.bankSlips && contract.bankSlips.length);
  });

  return validContracts.map((contract, index) => {
    const bankSlips = [...contract.bankSlips].sort((a, b) =>
      String(a.dueDate).localeCompare(String(b.dueDate))
    );

    // O rótulo genérico só entra quando o contrato não traz planName; nesse
    // caso numeramos para distinguir contratos diferentes na mesma tela.
    const fallbackTitle =
      validContracts.length > 1
        ? `${SAP_CONTRACT_TITLE} ${index + 1}`
        : SAP_CONTRACT_TITLE;

    return {
      offerTitle: contract.planName || fallbackTitle,
      transactionData: {
        // Sem offerPrice de propósito: o cabeçalho mostra só o nome do plano.
        installmentsCount: bankSlips.length,
        paidInstallmentsCount: bankSlips.filter(bankSlip => bankSlip.paid)
          .length
      },
      invoiceData: bankSlips.map(bankSlip => ({
        installmentDueDate: formatDueDate(bankSlip.dueDate),
        installmentIsPaid: Boolean(bankSlip.paid),
        installmentPrice: formatPrice(bankSlip.amount),
        installmentNumber: bankSlip.parcCheckout,
        digitableLine: bankSlip.digitableLine
      }))
    };
  });
};

/*
 * Busca e normaliza numa tacada só. Nunca rejeita: se o SAP estiver fora do ar,
 * devolve lista vazia para que a origem antiga continue sendo exibida.
 */
export const fetchSapBankSlipOrders = async () => {
  try {
    const response = await getSapBankSlips();
    return normalizeSapBankSlips(response);
  } catch (error) {
    console.error('Erro ao buscar boletos do SAP:', error);
    return [];
  }
};
